import { LogIn, LogOut, User as UserIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";

// Sign-in / account control for page headers. Renders nothing when auth
// isn't configured (env vars absent), so the app looks identical to today
// until Supabase is wired up. When configured: a "Sign in" button while
// logged out, and an avatar dropdown (email + sign out) while logged in.
//
// `variant`: the Dashboard uses the default pill at every width; the Trip
// top bar passes "compact", where the signed-out pill collapses to its icon
// below md (the mobile header has no room for it beside the masthead and
// three other 44px controls; the mobile artboard draws only icons there).
export default function AccountMenu({
  variant = "button",
}: {
  variant?: "button" | "compact";
}) {
  const { user, loading, isConfigured, signInWithGoogle, signOut } = useAuth();
  const navigate = useNavigate();

  // Sign out, then go home. Without the redirect, signing out while on a
  // trip page leaves you on a trip you no longer own (its data left with the
  // account), which renders a stuck "Loading trip..." screen.
  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  // Hidden entirely until auth is configured, or while the first session
  // check is still resolving (avoids a flash of "Sign in" for a user who
  // is actually logged in).
  if (!isConfigured || loading) return null;

  if (!user) {
    const compact = variant === "compact";
    return (
      <button
        type="button"
        onClick={() => signInWithGoogle()}
        aria-label="Sign in"
        className={`kicker inline-flex items-center justify-center gap-1.5 text-ink transition-colors ${
          compact
            ? "h-11 w-11 md:h-auto md:w-auto md:min-h-[44px] md:px-4 md:border md:border-ink md:hover:bg-ink md:hover:text-paper"
            : "min-h-[44px] px-4 border border-ink hover:bg-ink hover:text-paper"
        }`}
      >
        <LogIn className="h-4 w-4" />
        <span className={compact ? "hidden md:inline" : undefined}>Sign in</span>
      </button>
    );
  }

  const email = user.email ?? "Signed in";
  const avatarUrl =
    (user.user_metadata?.avatar_url as string | undefined) ?? undefined;
  const initial = (email[0] ?? "?").toUpperCase();

  // The panel leads with a name. Google gives us one; when it doesn't, the
  // part of the email before the @ reads far better than dropping the line
  // and leaving the address to carry the whole header on its own.
  const metadataName = (user.user_metadata?.full_name ??
    user.user_metadata?.name) as string | undefined;
  const displayName =
    metadataName?.trim() || (user.email ? user.email.split("@")[0] : "Signed in");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center h-11 w-11 shrink-0 -my-1.5 text-ink-muted hover:text-ink transition-colors"
          aria-label="Account"
        >
          <span className="inline-flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-ink text-paper">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt=""
                className="h-full w-full object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              <span className="kicker text-paper">{initial}</span>
            )}
          </span>
        </button>
      </DropdownMenuTrigger>
      {/* The design's panel, not the shadcn default: square corners, a 1px
          rule, the card surface, and rows that fill the width. p-0 kills the
          default inset padding so py-1.5 is the only vertical padding. */}
      <DropdownMenuContent
        align="end"
        className="w-[232px] rounded-none border-rule bg-white p-0 py-1.5 text-ink shadow-[0_18px_44px_-18px_rgba(var(--fn-shadow-rgb),0.3)]"
      >
        <div className="px-4 py-2.5 border-b border-rule-hair">
          <div className="font-serif text-[15px] text-ink truncate">
            {displayName}
          </div>
          <div className="text-[11.5px] text-ink-faint mt-0.5 truncate">
            {email}
          </div>
        </div>
        <DropdownMenuItem
          onSelect={() => navigate("/dashboard")}
          className="h-11 px-4 flex items-center gap-2.5 rounded-none text-[13px] text-ink cursor-pointer focus:bg-paper-warm focus:text-ink"
        >
          <UserIcon className="h-3.5 w-3.5 shrink-0" />
          My trips
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => handleSignOut()}
          className="h-11 px-4 flex items-center gap-2.5 rounded-none text-[13px] text-ink cursor-pointer focus:bg-paper-warm focus:text-ink"
        >
          <LogOut className="h-3.5 w-3.5 shrink-0" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
